import { Alert, Chip, Divider, tooltipClasses } from "@mui/material";
import { GridColDef, GridValidRowModel } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { createElement, useMemo } from "react";
import { TbUsersGroup } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import { softColors } from "@/constants/colors";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";
import { CommonStatusItem } from "@/types/global";

export function RenderEstimatedPeopleCountCell({
    companionsLength,
    value,
    isFlightArrival,
}: {
    companionsLength: number;
    value: number;
    isFlightArrival: boolean;
}) {
    const companionsCount = companionsLength + 1;
    const status: CommonStatusItem = useMemo(() => {
        const color =
            companionsCount === value ? (isFlightArrival ? softColors.indigo : softColors.green) : softColors.red;
        return {
            ...color,
            component: () => TbUsersGroup,
            tooltip: {
                title: "Yolcu Sayısı",
                description: `${value} yolcu bulunmaktadır.Yolcu sayısı, refakatçi sayısı ve yolcu toplamına eşittir. `,
            },
        };
    }, [value, isFlightArrival, companionsCount]);
    if (!status) return null;
    return (
        <div className="flex items-center py-1 h-full">
            <NoMaxWidthTooltip
                arrow
                sx={{
                    [`& .${tooltipClasses.tooltip}`]: {
                        backgroundColor: "white",
                        border: "none",
                        padding: "0px !important",
                        fontSize: 12,
                        maxWidth: "300px !important",
                    },
                }}
                title={
                    <HotelStatusTooltip status={status}>
                        {companionsCount !== value && (
                            <div>
                                <Divider />
                                <Alert sx={{ backgroundColor: "inherit !important" }} severity="error">
                                    Girilen <span className="font-semibold">kişi toplamı({value})</span>,{" "}
                                    <span className="font-semibold">refakatçi sayısı</span> ve{" "}
                                    <span className="font-semibold">yolcu sayısı({companionsCount})</span> toplamına
                                    eşleşmemektedir.
                                </Alert>
                            </div>
                        )}
                    </HotelStatusTooltip>
                }
            >
                <Chip
                    sx={{
                        backgroundColor: status.bg,
                        color: status.text,
                        fontWeight: 600,
                        fontSize: "12px !important",
                    }}
                    icon={createElement(TbUsersGroup, {
                        fontSize: 14,
                        color: status.text,
                    })}
                    label={String(value || 0)}
                />
            </NoMaxWidthTooltip>
        </div>
    );
}

const EstimatedPeopleCountColumn = <T extends GridValidRowModel>({
    t,
    getValueGetter,
    getCompanionsLength,
    getIsFlightArrival,
}: {
    t: TFunction;
    getIsFlightArrival: (row: T) => boolean;
    getValueGetter: (row: T) => number;
    getCompanionsLength: (row: T) => number;
}): GridColDef<T> => {
    return {
        field: "estimatedPeopleCount",
        headerName: t("estimatedPeopleCount"),
        minWidth: 140,
        valueGetter: (_val, row) => getValueGetter(row),
        renderHeader: (params) => <TableHeader Component={TbUsersGroup} title={params.colDef.headerName!} />,
        renderCell: ({ value, row }) => (
            <RenderEstimatedPeopleCountCell
                isFlightArrival={getIsFlightArrival(row)}
                value={value}
                companionsLength={getCompanionsLength(row)}
            />
        ),
    };
};
export default EstimatedPeopleCountColumn;
